import React, { useState, useEffect, useCallback, useContext } from "react";
import { Container, Row, Col, Card, Spinner, Alert, Button } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChartBar, 
  faTrophy, 
  faCheckCircle, 
  faFileAlt,
  faQuestionCircle 
} from "@fortawesome/free-solid-svg-icons";
import SwaggerContext from "../../SwaggerContext";
import { statisticsService } from "../../services/statisticsService";
import { useNavigate } from "react-router-dom";

// Constantes fuera del componente para evitar recreación
const CATEGORY_NAMES = {
  CAUSA_EMOCION: "Causa emoción",
  EMOCION_COMPLEJA: "Emoción compleja",
  DESEO_EMOCION: "Deseo Emoción",
  PENSAMIENTO_EMOCION: "Pensamiento emoción",
  INTERACCION: "Interacciones",
};

const CATEGORY_COLORS = {
  CAUSA_EMOCION: "#3498db",
  EMOCION_COMPLEJA: "#1abc9c",
  DESEO_EMOCION: "#f1c40f",
  PENSAMIENTO_EMOCION: "#e67e22",
  INTERACCION: "#e74c3c",
};

// Función para normalizar sessionType
const normalizeSessionType = (sessionType) => {
  if (!sessionType) return sessionType;
  
  if (sessionType.includes(",")) {
    const parts = sessionType.split(",").map(s => s.trim()).sort();
    return parts.join(",");
  }
  
  return sessionType;
};

// Función para obtener el nombre de la categoría
const getCategoryName = (sessionType) => {
  const normalized = normalizeSessionType(sessionType);
  
  const allCategories = ["CAUSA_EMOCION", "EMOCION_COMPLEJA", "DESEO_EMOCION", "PENSAMIENTO_EMOCION", "INTERACCION"];
  if (normalized && normalized.includes(",")) {
    const types = normalized.split(",").map(s => s.trim()).sort();
    const isAllCategories = allCategories.every(cat => types.includes(cat)) && types.length === allCategories.length;
    if (isAllCategories) {
      return "Todas";
    }
  }
  
  return CATEGORY_NAMES[normalized] || sessionType;
};

// Función para obtener el color de la categoría
const getCategoryColor = (sessionType) => {
  const normalized = normalizeSessionType(sessionType);
  
  const allCategories = ["CAUSA_EMOCION", "EMOCION_COMPLEJA", "DESEO_EMOCION", "PENSAMIENTO_EMOCION", "INTERACCION"];
  if (normalized && normalized.includes(",")) {
    const types = normalized.split(",").map(s => s.trim()).sort();
    const isAllCategories = allCategories.every(cat => types.includes(cat)) && types.length === allCategories.length;
    if (isAllCategories) {
      return "#c0392b";
    }
  }
  
  return CATEGORY_COLORS[normalized] || "#95a5a6";
};

const Statistics = () => {
  const { gameSessionApi } = useContext(SwaggerContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadStatistics = useCallback(async () => {
    setError(null);
    setLoading(true);
    const result = await statisticsService.getUserStatistics(gameSessionApi);
    
    if (result.success) {
      setStats(result.data);
    } else {
      setError(result.error);
    }
    setLoading(false);
  }, [gameSessionApi]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", { 
      day: "2-digit", 
      month: "2-digit", 
      year: "numeric" 
    });
  }, []);

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: "80vh" }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Cargando estadísticas...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <Alert.Heading>Error al cargar estadísticas</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={loadStatistics}>
            Reintentar
          </Button>
        </Alert>
      </Container>
    );
  }

  if (!stats || stats.totalSessions === 0) {
    return (
      <Container className="mt-5 text-center">
        <FontAwesomeIcon icon={faChartBar} size="4x" className="text-muted mb-4" />
        <h2>Sin estadísticas aún</h2>
        <p className="text-muted mb-4">
          Comienza a jugar para ver tu progreso y estadísticas
        </p>
        <Button variant="primary" onClick={() => navigate("/modo")}>
          Ir a jugar
        </Button>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <div className="text-center mb-4">
        <FontAwesomeIcon icon={faChartBar} size="3x" className="text-primary mb-3" />
        <h1>Mis Estadísticas</h1>
        <p className="text-muted">Desde {formatDate(stats.firstActivity)}</p>
      </div>

      <h3 className="mb-3">Resumen Global</h3>
      <Row className="mb-4">
        <Col md={4} className="mb-3">
          <Card>
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faTrophy} size="2x" className="text-warning mb-2" />
              <h5>Sesiones Completadas</h5>
              <h2 className="mb-0">{stats.completedSessions}</h2>
              <small className="text-muted">de {stats.totalSessions} totales</small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-3">
          <Card>
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faCheckCircle} size="2x" className="text-success mb-2" />
              <h5>Tasa de Acierto</h5>
              <h2 className="mb-0">{stats.overallAccuracyRate.toFixed(1)}%</h2>
              <small className="text-muted">
                {stats.totalCorrectAnswers} de {stats.totalQuestionsAnswered} preguntas
              </small>
            </Card.Body>
          </Card>
        </Col>

        <Col md={4} className="mb-3">
          <Card>
            <Card.Body className="text-center">
              <FontAwesomeIcon icon={faFileAlt} size="2x" className="text-primary mb-2" />
              <h5>Páginas Visitadas</h5>
              <h2 className="mb-0">{stats.totalPagesVisited}</h2>
              <small className="text-muted">lecciones completadas</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {stats.categoryStatistics && stats.categoryStatistics.length > 0 && (
        <>
          <h3 className="mb-3">Por Categoría</h3>
          <Row>
            {stats.categoryStatistics.map((category, index) => {
              const categoryName = getCategoryName(category.sessionType);
              const categoryColor = getCategoryColor(category.sessionType);
              
              return (
                <Col md={6} lg={4} key={index} className="mb-4">
                  <Card>
                    <Card.Header 
                      style={{ backgroundColor: categoryColor, color: "white" }}
                      className="d-flex justify-content-between align-items-center"
                    >
                      <strong>{categoryName}</strong>
                      <span className="badge bg-light text-dark">
                        {category.completedSessions}/{category.totalSessions}
                      </span>
                    </Card.Header>
                    <Card.Body>
                      <Row className="text-center mb-3">
                        <Col xs={4}>
                          <FontAwesomeIcon icon={faTrophy} className="text-warning mb-1" />
                          <div className="small text-muted">Completadas</div>
                          <strong>{category.completedSessions}</strong>
                        </Col>
                        <Col xs={4}>
                          <FontAwesomeIcon icon={faCheckCircle} className="text-success mb-1" />
                          <div className="small text-muted">Acierto</div>
                          <strong>{category.accuracyRate.toFixed(1)}%</strong>
                        </Col>
                        <Col xs={4}>
                          <FontAwesomeIcon icon={faQuestionCircle} className="text-primary mb-1" />
                          <div className="small text-muted">Preguntas</div>
                          <strong>{category.totalQuestionsAnswered}</strong>
                        </Col>
                      </Row>
                      <div className="text-center border-top pt-2">
                        <small className="text-muted">
                          Última actividad: {formatDate(category.lastActivity)}
                        </small>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </>
      )}

      <Card className="mt-4 bg-light">
        <Card.Body className="text-center">
          <p className="mb-0">
            ¡Sigue así! 🎉 Cada sesión te acerca más a comprender todas las emociones
          </p>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Statistics;
